import { router } from '@inertiajs/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import DriveSyncLayout from '../../layouts/DriveSyncLayout';
import DriversHeader from './DriversHeader';

export default function Edit({ driver, carriers = [], errors = {} }) {
  const [formData, setFormData] = React.useState({
    name: driver.name || '',
    email: driver.email || '',
    phone: driver.phone || '',
    password: '',
    carrier_id: driver.carrier_id || ''
  });
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);

  // Sync server errors with local state when props change
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
    }
  }, [errors]);

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing (both client-side and server errors)
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (!formData.carrier_id) {
      errors.carrier_id = 'Transportadora é obrigatória';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        carrier_id: formData.carrier_id
      };

      // Only include password if it's provided
      if (formData.password.trim()) {
        submitData.password = formData.password;
      }

      router.put(`/drivers/${driver.id}`, { user: submitData });
    } catch (error) {
      console.error('Error updating driver:', error);
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.get('/drivers');
  };

  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhone(event.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  return (
    <DriveSyncLayout>
      <DriversHeader />

      <Box sx={{
        width: '100%',
        maxWidth: { sm: '100%', md: '800px' },
        mx: 'auto',
        mt: 2
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Editar Motorista
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Edite as informações do motorista.
        </Typography>

        {/* Error Alert */}
        {getValidErrors().length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              <strong>Foram encontrados {getValidErrors().length} erro(s):</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {getValidErrors().map((error, index) => (
                <Box component="li" key={index}>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* Form */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6">
                Informações do Motorista
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Nome completo do motorista"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  required
                />

                <TextField
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="motorista@email.com"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  required
                />

                <TextField
                  id="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                />

                <TextField
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  placeholder="Nova Senha"
                  error={!!formErrors.password}
                  helperText={formErrors.password || 'Deixe em branco para manter a senha atual'}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                <FormControl variant="outlined" fullWidth error={!!formErrors.carrier_id} required>
                  <InputLabel id="carrier-label">Transportadora</InputLabel>
                  <Select
                    labelId="carrier-label"
                    value={formData.carrier_id}
                    onChange={handleInputChange('carrier_id')}
                    disabled={loading}
                    label="Transportadora"
                  >
                    {carriers.map((carrier) => (
                      <MenuItem key={carrier.id} value={carrier.id}>
                        {carrier.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.carrier_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                      {formErrors.carrier_id}
                    </Typography>
                  )}
                </FormControl>

                {/* Driver Role Information */}
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DirectionsCarIcon color="primary" />
                    <Typography variant="h6">
                      Informações do Perfil
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Este usuário possui o perfil de <strong>Motorista</strong> e tem acesso apenas às funcionalidades específicas para condutores.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </Box>
      </Box>
    </DriveSyncLayout>
  );
} 